import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  ResponsiveContainer, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceDot, ReferenceArea,
  PieChart, Pie, Cell,
} from 'recharts';
import {
  dashApi,
  MONTH_NAMES, YEAR_OPTIONS,
  getCurrentMonthName, getPrevMonthName,
  formatAmount, toCr, calcMoM,
  formatTrendData, mergeTrendArrays, peakAndLow,
  triggerDownload,
} from '../../../api/dashApi';

// ─── Sub-components ───────────────────────────────────────────────────────────

const KpiCard = ({ label, value, sub, change, color = 'white' }) => {
  const isBlue = color === 'blue';
  const up = change != null && change >= 0;
  return (
    <div className={`rounded-xl p-4 border relative overflow-hidden ${
      isBlue
        ? 'bg-primary text-white border-primary'
        : 'bg-white border-gray-100 shadow-sm'
    }`}>
      {isBlue && (
        <div className="absolute -right-3 -top-3 w-16 h-16 rounded-full bg-white/10" />
      )}
      <div className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isBlue ? 'text-white/70' : 'text-gray-500'}`}>
        {label}
      </div>
      <div className={`text-2xl font-extrabold tracking-tight ${isBlue ? 'text-white' : 'text-gray-900'}`}>
        {value || '—'}
      </div>
      <div className="flex items-center gap-2 mt-2">
        {change != null && (
          <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
            isBlue
              ? 'bg-white/20 text-white'
              : up ? 'text-green-700 bg-green-50' : 'text-red-600 bg-red-50'
          }`}>
            {up ? '↑' : '↓'} {Math.abs(change)}%
          </span>
        )}
        {sub && (
          <span className={`text-xs ${isBlue ? 'text-white/60' : 'text-gray-400'}`}>{sub}</span>
        )}
      </div>
    </div>
  );
};

const MomHighlight = ({ items, bg, border }) => (
  <div className={`flex items-stretch gap-0 ${bg} border ${border} rounded-lg mb-3 overflow-hidden`}>
    {items.map((item, i) => (
      <div key={i} className={`flex-1 px-3 py-2.5 ${i < items.length - 1 ? `border-r ${border}` : ''}`}>
        <div className="text-xs text-gray-400 font-medium mb-1">{item.label}</div>
        <div className="flex items-baseline gap-1.5">
          <span className={`text-xl font-extrabold tracking-tight ${item.color}`}>
            {item.change != null ? `${item.change >= 0 ? '↑' : '↓'} ${Math.abs(item.change)}%` : '—'}
          </span>
          <span className="text-xs font-semibold text-gray-700">{item.value}</span>
        </div>
      </div>
    ))}
  </div>
);

const PeakLowStrip = ({ peak, low, ecmsKey, fleetKey, peakBg, lowBg, peakBorder, lowBorder, unit = 'Cr' }) => (
  <div className="flex gap-2 mt-2 mb-2">
    <div className={`flex-1 flex items-center gap-2 ${peakBg} border ${peakBorder} rounded-lg px-3 py-2`}>
      <span className="text-lg leading-none text-green-600">▲</span>
      <div>
        <div className="text-xs text-gray-500 font-medium">Peak</div>
        <div className="text-xs font-bold text-gray-900">{peak?.label || '—'}</div>
      </div>
      <div className="ml-auto text-right">
        {ecmsKey && <div className="text-xs font-bold text-primary">₹{peak?.[ecmsKey]} {unit} <span className="text-gray-400 font-normal text-xs">ECMS</span></div>}
        {fleetKey && <div className="text-xs font-bold text-green-600">₹{peak?.[fleetKey]} {unit} <span className="text-gray-400 font-normal text-xs">iFleet</span></div>}
      </div>
    </div>
    <div className={`flex-1 flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2`}>
      <span className="text-lg leading-none text-yellow-600">▼</span>
      <div>
        <div className="text-xs text-gray-500 font-medium">Lowest</div>
        <div className="text-xs font-bold text-gray-900">{low?.label || '—'}</div>
      </div>
      <div className="ml-auto text-right">
        {ecmsKey && <div className="text-xs font-bold text-primary">₹{low?.[ecmsKey]} {unit} <span className="text-gray-400 font-normal text-xs">ECMS</span></div>}
        {fleetKey && <div className="text-xs font-bold text-green-600">₹{low?.[fleetKey]} {unit} <span className="text-gray-400 font-normal text-xs">iFleet</span></div>}
      </div>
    </div>
  </div>
);

const MomHistoryStrip = ({ history, currentLabel, activeColor }) => (
  <div className="flex items-center gap-1.5 pt-2 border-t border-gray-50 flex-wrap">
    <span className="text-xs text-gray-400 font-medium flex-shrink-0">History:</span>
    {history.map((h, i) => {
      const isCurrent = h.label === currentLabel;
      const isNeg = h.change < 0;
      if (isCurrent) {
        return (
          <span key={i} className={`text-xs px-2 py-0.5 rounded font-bold text-white shadow-sm ${activeColor}`}>
            {h.label} {h.change >= 0 ? '↑' : '↓'}{Math.abs(h.change)}% ★
          </span>
        );
      }
      return (
        <span key={i} className={`text-xs px-1.5 py-0.5 rounded font-medium ${
          isNeg ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-500'
        }`}>
          {h.label} {h.change >= 0 ? '↑' : '↓'}{Math.abs(h.change)}%
        </span>
      );
    })}
  </div>
);

const Top5Table = ({ title, subtitle, data, downloadLabel, onDownload, colorClass, borderClass }) => {
  const maxAmt = data?.[0]?.amount ?? 1;
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-sm font-bold text-gray-900">{title}</div>
          <div className="text-xs text-gray-400 mt-0.5">{subtitle}</div>
        </div>
        {onDownload && (
          <button onClick={onDownload}
            className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-lg px-3 py-1.5 font-medium hover:bg-blue-100 transition-colors">
            ⬇ XLS
          </button>
        )}
      </div>
      {!data || data.length === 0 ? (
        <div className="text-center text-gray-400 text-sm py-8">No data for selected period</div>
      ) : (
        <div className="flex flex-col gap-3">
          {data.map((row, i) => {
            const pct = maxAmt > 0 ? (row.amount / maxAmt) * 100 : 0;
            return (
              <div key={i}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-5 h-5 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0 ${colorClass[i] || colorClass[colorClass.length - 1]}`}>
                      {i + 1}
                    </span>
                    <span className="text-sm text-gray-700 font-medium truncate max-w-[160px]">{row.enterpriseName}</span>
                  </div>
                  <span className="text-sm font-bold text-gray-900 flex-shrink-0">{formatAmount(row.amount)}</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${borderClass}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const DashHome = () => {
  const [selectedYear,  setSelectedYear]  = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthName());

  // Raw API data
  const [ecmsPool,  setEcmsPool]  = useState(null);
  const [fleetPool, setFleetPool] = useState(null);
  const [ecmsPf,    setEcmsPf]    = useState(null);

  const [ecmsPoolTrendRaw,  setEcmsPoolTrendRaw]  = useState(null);
  const [fleetPoolTrendRaw, setFleetPoolTrendRaw] = useState(null);
  const [ecmsPfTrendRaw,    setEcmsPfTrendRaw]    = useState(null);

  const [loading,      setLoading]      = useState(true);
  const [trendLoading, setTrendLoading] = useState(true);
  const [cachedAt,     setCachedAt]     = useState(null);

  // New stats
  const [liveStats,      setLiveStats]      = useState(null);
  const [monthStats,     setMonthStats]     = useState(null);
  const [liveLoading,    setLiveLoading]    = useState(true);
  const [monthLoading,   setMonthLoading]   = useState(true);

  // Pool loading day-wise for selected month
  const [poolDayTrend,    setPoolDayTrend]    = useState(null);
  const [poolDayLoading,  setPoolDayLoading]  = useState(true);
  const [poolDayView,     setPoolDayView]     = useState('all'); // 'all' | 'ecms' | 'fleet'

  // Chart toggles
  const [poolView, setPoolView] = useState('all'); // 'all' | 'ecms' | 'fleet'
  const [pfView,   setPfView]   = useState('all');

  // Drill-down state — Pool Loading Trend
  const [poolDrillLevel,   setPoolDrillLevel]   = useState('month'); // 'month' | 'day' | 'hour'
  const [poolDrillMonth,   setPoolDrillMonth]   = useState(null);
  const [poolDrillDay,     setPoolDrillDay]     = useState(null);
  const [poolDrillData,    setPoolDrillData]    = useState(null);
  const [poolDrillLoading, setPoolDrillLoading] = useState(false);

  // Drill-down state — Platform Fee Trend
  const [pfDrillLevel,   setPfDrillLevel]   = useState('month');
  const [pfDrillMonth,   setPfDrillMonth]   = useState(null);
  const [pfDrillDay,     setPfDrillDay]     = useState(null);
  const [pfDrillData,    setPfDrillData]    = useState(null);
  const [pfDrillLoading, setPfDrillLoading] = useState(false);

  // Drag-to-zoom — Pool (refs = no re-render on mouseDown/Up, only state for overlay)
  const [poolRefLeft,  setPoolRefLeft]  = useState('');
  const [poolRefRight, setPoolRefRight] = useState('');
  const [poolZoomed,   setPoolZoomed]   = useState(null); // null = show all months
  const poolDragStartRef  = useRef('');   // label where drag began
  const poolDragEndRef    = useRef('');   // label where drag ended
  const poolIsDragging    = useRef(false);
  const poolDidDrag       = useRef(false); // true when mouseUp completed a real drag → suppresses onClick

  // Drag-to-zoom — PF
  const [pfRefLeft,  setPfRefLeft]  = useState('');
  const [pfRefRight, setPfRefRight] = useState('');
  const [pfZoomed,   setPfZoomed]   = useState(null);
  const pfDragStartRef = useRef('');
  const pfDragEndRef   = useRef('');
  const pfIsDragging   = useRef(false);
  const pfDidDrag      = useRef(false);

  // Fetch KPI + Top5 data when year changes
  const fetchKpi = useCallback(async (year) => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        dashApi.getEcmsPoolTop5(year),
        dashApi.getFleetPoolTop5(year),
        dashApi.getEcmsPfTop5(year),
      ]);
      setEcmsPool(r1.data);
      setFleetPool(r2.data);
      setEcmsPf(r3.data);
      if (r1.data?.cachedAt) setCachedAt(r1.data.cachedAt);
    } catch (e) {
      console.error('Dash KPI fetch error', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch trend data once
  const fetchTrends = useCallback(async () => {
    setTrendLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        dashApi.getEcmsPoolTrend(),
        dashApi.getFleetPoolTrend(),
        dashApi.getEcmsPfTrend(),
      ]);
      setEcmsPoolTrendRaw(r1.data);
      setFleetPoolTrendRaw(r2.data);
      setEcmsPfTrendRaw(r3.data);
    } catch (e) {
      console.error('Dash trend fetch error', e);
    } finally {
      setTrendLoading(false);
    }
  }, []);

  // Fetch live stats once on mount
  useEffect(() => {
    dashApi.getLiveStats()
      .then(r => setLiveStats(r.data))
      .catch(e => console.error('liveStats fetch error', e))
      .finally(() => setLiveLoading(false));
  }, []);

  // Fetch month stats + pool day trend whenever selected month/year changes
  useEffect(() => {
    const monthIdx  = MONTH_NAMES.indexOf(selectedMonth) + 1;
    const monthStr  = `${selectedYear}-${String(monthIdx).padStart(2, '0')}`;
    setMonthLoading(true);
    setPoolDayLoading(true);
    dashApi.getMonthStats(monthStr)
      .then(r => setMonthStats(r.data))
      .catch(e => console.error('monthStats fetch error', e))
      .finally(() => setMonthLoading(false));
    dashApi.getPoolDayStats(monthStr)
      .then(r => setPoolDayTrend(r.data))
      .catch(e => console.error('poolDayTrend fetch error', e))
      .finally(() => setPoolDayLoading(false));
  }, [selectedMonth, selectedYear]);

  useEffect(() => { fetchKpi(selectedYear); }, [selectedYear, fetchKpi]);
  useEffect(() => { fetchTrends(); }, [fetchTrends]);

  // ── Derived data ────────────────────────────────────────────────────────────
  const prevMonth = getPrevMonthName(selectedMonth);

  const kpi = useMemo(() => {
    const ec   = ecmsPool?.TotalPoolAccountLoading?.[selectedMonth] ?? 0;
    const fl   = fleetPool?.TotalPoolAccountLoading?.[selectedMonth] ?? 0;
    const pf   = ecmsPf?.TotalPlatformFees?.[selectedMonth] ?? 0;
    const ecP  = ecmsPool?.TotalPoolAccountLoading?.[prevMonth] ?? 0;
    const flP  = fleetPool?.TotalPoolAccountLoading?.[prevMonth] ?? 0;
    const pfP  = ecmsPf?.TotalPlatformFees?.[prevMonth] ?? 0;
    return {
      total:    ec + fl,
      ecms:     ec,
      fleet:    fl,
      pf,
      momTotal: calcMoM(ec + fl, ecP + flP),
      momEcms:  calcMoM(ec, ecP),
      momFleet: calcMoM(fl, flP),
      momPf:    calcMoM(pf, pfP),
    };
  }, [ecmsPool, fleetPool, ecmsPf, selectedMonth, prevMonth]);

  const top5Ecms  = useMemo(() => ecmsPool?.top5PoolAccountLoading?.[selectedMonth] ?? [], [ecmsPool, selectedMonth]);
  const top5Fleet = useMemo(() => fleetPool?.top5PoolAccountLoading?.[selectedMonth] ?? [], [fleetPool, selectedMonth]);

  // Trend data for charts
  const poolTrend = useMemo(() => {
    const ecmsArr  = formatTrendData(ecmsPoolTrendRaw);
    const fleetArr = formatTrendData(fleetPoolTrendRaw);
    return mergeTrendArrays(ecmsArr, fleetArr);
  }, [ecmsPoolTrendRaw, fleetPoolTrendRaw]);

  const pfTrend = useMemo(() => {
    const ecmsArr = formatTrendData(ecmsPfTrendRaw);
    const merged  = mergeTrendArrays(ecmsArr, []); // fleet PF is 0 currently
    // Convert Cr → Lakhs (×100) so the chart Y-axis is readable
    return merged.map(d => ({
      ...d,
      ecms:     parseFloat((d.ecms     * 100).toFixed(2)),
      combined: parseFloat((d.combined * 100).toFixed(2)),
    }));
  }, [ecmsPfTrendRaw]);

  // Peak & Low
  const { peak: poolPeak, low: poolLow } = useMemo(() => peakAndLow(poolTrend, 'ecms'), [poolTrend]);
  const { peak: pfPeak,   low: pfLow   } = useMemo(() => peakAndLow(pfTrend,   'ecms'), [pfTrend]);

  // MoM history strips (last 7 non-null MoM entries)
  const poolMomHistory = useMemo(() => {
    if (poolTrend.length < 2) return [];
    return poolTrend.slice(1).map((d, i) => ({
      label:  d.label,
      change: calcMoM(d.ecms, poolTrend[i].ecms),
    })).filter(h => h.change != null);
  }, [poolTrend]);

  const pfMomHistory = useMemo(() => {
    if (pfTrend.length < 2) return [];
    return pfTrend.slice(1).map((d, i) => ({
      label:  d.label,
      change: calcMoM(d.ecms, pfTrend[i].ecms),
    })).filter(h => h.change != null);
  }, [pfTrend]);

  // Current month label for history strip
  const currentLabel = useMemo(() => {
    if (poolTrend.length === 0) return '';
    const found = poolTrend.find(d => d.month === selectedMonth && d.year === selectedYear);
    return found?.label || poolTrend[poolTrend.length - 1]?.label || '';
  }, [poolTrend, selectedMonth, selectedYear]);

  // ── Drag-to-zoom handlers — Pool ────────────────────────────────────────
  // mouseDown: store start in a ref — NO setState → no re-render → onClick still works
  const handlePoolMouseDown = (e) => {
    poolDidDrag.current    = false;     // always clear — browser skips onClick after real drag
    poolIsDragging.current = false;
    if (!e?.activeLabel) return;
    poolDragStartRef.current = e.activeLabel;
    poolDragEndRef.current   = e.activeLabel;
    poolIsDragging.current   = true;
  };
  // mouseMove: user is genuinely dragging — now it's safe to setState for the overlay
  const handlePoolMouseMove = (e) => {
    if (!poolIsDragging.current || !e?.activeLabel) return;
    if (e.activeLabel === poolDragStartRef.current && !poolRefLeft) return; // haven't moved yet
    poolDragEndRef.current = e.activeLabel;
    setPoolRefLeft(poolDragStartRef.current);
    setPoolRefRight(e.activeLabel);
  };
  const handlePoolMouseUp = () => {
    if (!poolIsDragging.current) return;
    poolIsDragging.current = false;
    const left  = poolDragStartRef.current;
    const right = poolDragEndRef.current;
    setPoolRefLeft(''); setPoolRefRight('');
    if (!left || !right || left === right) return; // single click — let onClick handle it
    const src = poolZoomed ?? poolTrend;
    const li = src.findIndex(d => d.label === left);
    const ri = src.findIndex(d => d.label === right);
    if (li === -1 || ri === -1 || li === ri) return;
    const [lo, hi] = li <= ri ? [li, ri] : [ri, li];
    setPoolZoomed((poolZoomed ?? poolTrend).slice(lo, hi + 1));
    poolDidDrag.current = true; // suppress the upcoming onClick
  };

  // ── Drag-to-zoom handlers — PF ───────────────────────────────────────────
  const handlePfMouseDown = (e) => {
    pfDidDrag.current    = false;
    pfIsDragging.current = false;
    if (!e?.activeLabel) return;
    pfDragStartRef.current = e.activeLabel;
    pfDragEndRef.current   = e.activeLabel;
    pfIsDragging.current   = true;
  };
  const handlePfMouseMove = (e) => {
    if (!pfIsDragging.current || !e?.activeLabel) return;
    if (e.activeLabel === pfDragStartRef.current && !pfRefLeft) return;
    pfDragEndRef.current = e.activeLabel;
    setPfRefLeft(pfDragStartRef.current);
    setPfRefRight(e.activeLabel);
  };
  const handlePfMouseUp = () => {
    if (!pfIsDragging.current) return;
    pfIsDragging.current = false;
    const left  = pfDragStartRef.current;
    const right = pfDragEndRef.current;
    setPfRefLeft(''); setPfRefRight('');
    if (!left || !right || left === right) return;
    const src = pfZoomed ?? pfTrend;
    const li = src.findIndex(d => d.label === left);
    const ri = src.findIndex(d => d.label === right);
    if (li === -1 || ri === -1 || li === ri) return;
    const [lo, hi] = li <= ri ? [li, ri] : [ri, li];
    setPfZoomed((pfZoomed ?? pfTrend).slice(lo, hi + 1));
    pfDidDrag.current = true;
  };

  // ── Drill-down handlers ───────────────────────────────────────────────────
  const handlePoolChartClick = async (data) => {
    if (poolDidDrag.current) { poolDidDrag.current = false; return; } // suppress click after drag
    if (!data?.activePayload?.[0]) return;
    const payload = data.activePayload[0].payload;
    if (poolDrillLevel === 'month' && payload.monthStr) {
      setPoolDrillLoading(true);
      try {
        const res = await dashApi.getPoolDayStats(payload.monthStr);
        setPoolDrillData(res.data);
        setPoolDrillMonth(payload.monthStr);
        setPoolDrillLevel('day');
      } catch (e) { console.error('poolDayStats error', e); }
      finally { setPoolDrillLoading(false); }
    } else if (poolDrillLevel === 'day' && payload.dayDate) {
      setPoolDrillLoading(true);
      try {
        const res = await dashApi.getPoolHourStats(payload.dayDate);
        setPoolDrillData(res.data);
        setPoolDrillDay(payload.dayDate);
        setPoolDrillLevel('hour');
      } catch (e) { console.error('poolHourStats error', e); }
      finally { setPoolDrillLoading(false); }
    }
  };

  const resetPoolDrill = () => { setPoolDrillLevel('month'); setPoolDrillMonth(null); setPoolDrillDay(null); setPoolDrillData(null); };
  const poolBackToMonth = () => { setPoolDrillLevel('month'); setPoolDrillMonth(null); setPoolDrillDay(null); setPoolDrillData(null); };
  const poolBackToDay   = async () => {
    if (!poolDrillMonth) return;
    setPoolDrillLoading(true);
    try { const res = await dashApi.getPoolDayStats(poolDrillMonth); setPoolDrillData(res.data); setPoolDrillDay(null); setPoolDrillLevel('day'); }
    catch (e) { console.error(e); } finally { setPoolDrillLoading(false); }
  };

  const handlePfChartClick = async (data) => {
    if (pfDidDrag.current) { pfDidDrag.current = false; return; }
    if (!data?.activePayload?.[0]) return;
    const payload = data.activePayload[0].payload;
    if (pfDrillLevel === 'month' && payload.monthStr) {
      setPfDrillLoading(true);
      try {
        const res = await dashApi.getPfDayStats(payload.monthStr);
        setPfDrillData(res.data);
        setPfDrillMonth(payload.monthStr);
        setPfDrillLevel('day');
      } catch (e) { console.error('pfDayStats error', e); }
      finally { setPfDrillLoading(false); }
    } else if (pfDrillLevel === 'day' && payload.dayDate) {
      setPfDrillLoading(true);
      try {
        const res = await dashApi.getPfHourStats(payload.dayDate);
        setPfDrillData(res.data);
        setPfDrillDay(payload.dayDate);
        setPfDrillLevel('hour');
      } catch (e) { console.error('pfHourStats error', e); }
      finally { setPfDrillLoading(false); }
    }
  };

  const resetPfDrill = () => { setPfDrillLevel('month'); setPfDrillMonth(null); setPfDrillDay(null); setPfDrillData(null); };
  const pfBackToMonth = () => { setPfDrillLevel('month'); setPfDrillMonth(null); setPfDrillDay(null); setPfDrillData(null); };
  const pfBackToDay   = async () => {
    if (!pfDrillMonth) return;
    setPfDrillLoading(true);
    try { const res = await dashApi.getPfDayStats(pfDrillMonth); setPfDrillData(res.data); setPfDrillDay(null); setPfDrillLevel('day'); }
    catch (e) { console.error(e); } finally { setPfDrillLoading(false); }
  };

  // Download handlers
  const handleDownloadEcms = async () => {
    try { const r = await dashApi.downloadEcmsTop5(selectedYear); triggerDownload(r.data, `ecms-top5-${selectedYear}.xlsx`); }
    catch { /* silent */ }
  };
  const handleDownloadFleet = async () => {
    try { const r = await dashApi.downloadFleetTop5(selectedYear); triggerDownload(r.data, `fleet-top5-${selectedYear}.xlsx`); }
    catch { /* silent */ }
  };

  // ── Chart helpers ────────────────────────────────────────────────────────────
  const amtFormatter = v => v != null ? `₹${v} Cr` : '';
  const yFormatter   = v => `${v}Cr`;

  const ToggleBtn = ({ active, label, onClick, activeClass }) => (
    <button onClick={onClick}
      className={`px-2.5 py-1 text-xs font-medium border-none cursor-pointer transition-colors ${
        active ? `${activeClass} text-white font-semibold` : 'bg-white text-gray-500'
      }`}>
      {label}
    </button>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      <div className="w-full px-4 pt-6">

        {/* Header */}
        <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-extrabold text-gray-900 tracking-tight">Business Overview</h1>
            <p className="text-xs text-gray-400 mt-0.5">OmniCard B2B · ECMS &amp; iFleet consolidated view</p>
            {cachedAt && (
              <p className="text-xs text-gray-400 italic mt-0.5">Cached at: {cachedAt}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-3 py-1.5">
              <span className="text-xs text-gray-500">Month</span>
              <select
                value={selectedMonth}
                onChange={e => setSelectedMonth(e.target.value)}
                className="border-none outline-none text-xs font-semibold text-gray-800 bg-transparent cursor-pointer"
              >
                {MONTH_NAMES.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-3 py-1.5">
              <span className="text-xs text-gray-500">Year</span>
              <select
                value={selectedYear}
                onChange={e => setSelectedYear(Number(e.target.value))}
                className="border-none outline-none text-xs font-semibold text-gray-800 bg-transparent cursor-pointer"
              >
                {YEAR_OPTIONS.map(y => <option key={y}>{y}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* ── KPI Cards ── */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-28 bg-white rounded-xl animate-pulse border border-gray-100" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <KpiCard label="Total Pool Loading" value={formatAmount(kpi.total)} sub={`${selectedMonth} ${selectedYear} · ECMS + iFleet`} change={kpi.momTotal} color="blue" />
            <KpiCard label="ECMS Pool"           value={formatAmount(kpi.ecms)}  sub={`vs ${prevMonth}`}  change={kpi.momEcms} />
            <KpiCard label="iFleet Pool"         value={formatAmount(kpi.fleet)} sub={`vs ${prevMonth}`}  change={kpi.momFleet} />
            <KpiCard label="Platform Revenue"    value={formatAmount(kpi.pf)}    sub={`ECMS platform fee`} change={kpi.momPf} />
          </div>
        )}

        {/* ── Section A: Today's Loading ── */}
        <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm mb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-sm font-bold text-gray-900">Today's Loading</div>
              <div className="text-xs text-gray-400">
                {liveStats?.cachedAt ? `As of ${liveStats.cachedAt}` : 'Live — 12AM to now'}
              </div>
            </div>
            <span className="text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-1 rounded-lg font-medium">Live</span>
          </div>
          {liveLoading ? (
            <div className="grid grid-cols-3 gap-3">
              {[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />)}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'ECMS',    value: liveStats?.todayEcms,  color: 'text-primary' },
                { label: 'iFleet',  value: liveStats?.todayFleet, color: 'text-green-600' },
                { label: 'Total',   value: liveStats?.todayTotal, color: 'text-gray-900', bold: true },
              ].map(({ label, value, color, bold }) => (
                <div key={label} className={`rounded-lg px-4 py-3 ${bold ? 'bg-blue-50 border border-blue-100' : 'bg-gray-50 border border-gray-100'}`}>
                  <div className="text-xs text-gray-500 font-medium mb-1">{label}</div>
                  <div className={`text-lg font-extrabold ${color}`}>{formatAmount(value ?? 0)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Two Trend Charts ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">

          {/* Pool Loading Trend */}
          <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
            <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
              <div>
                <div className="text-sm font-bold text-gray-900">Pool Loading Trend</div>
                {poolDrillLevel === 'month' && <div className="text-xs text-gray-400">12-month view · click a point to drill down</div>}
                {poolDrillLevel === 'day'   && <div className="text-xs text-gray-400">Day-wise · {poolDrillMonth}</div>}
                {poolDrillLevel === 'hour'  && <div className="text-xs text-gray-400">Hour-wise · {poolDrillDay}</div>}
              </div>
              <div className="flex flex-col items-end gap-2">
                {poolDrillLevel === 'month' && (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1 text-xs text-gray-600"><span className="inline-block w-4 h-0.5 bg-primary rounded" />ECMS</div>
                      <div className="flex items-center gap-1 text-xs text-gray-600"><span className="inline-block w-4 h-0.5 bg-green-500 rounded" />iFleet</div>
                    </div>
                    <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                      <ToggleBtn active={poolView==='all'}   label="Both"   onClick={()=>setPoolView('all')}   activeClass="bg-primary" />
                      <ToggleBtn active={poolView==='ecms'}  label="ECMS"   onClick={()=>setPoolView('ecms')}  activeClass="bg-primary" />
                      <ToggleBtn active={poolView==='fleet'} label="iFleet" onClick={()=>setPoolView('fleet')} activeClass="bg-green-600" />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Zoom reset */}
            {poolDrillLevel === 'month' && poolZoomed && (
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-400">Zoomed view</span>
                <button onClick={() => setPoolZoomed(null)}
                  className="text-xs text-blue-600 border border-blue-200 bg-blue-50 hover:bg-blue-100 rounded px-2 py-0.5 font-medium transition-colors">
                  ✕ Reset Zoom
                </button>
              </div>
            )}

            {/* Breadcrumb */}
            {poolDrillLevel !== 'month' && (
              <div className="flex items-center gap-2 mb-2 text-xs">
                <button onClick={poolBackToMonth} className="text-blue-500 hover:text-blue-700 font-medium">← Back</button>
                <span className="text-gray-400">
                  <span className="cursor-pointer text-blue-500 hover:underline" onClick={poolBackToMonth}>All Months</span>
                  {poolDrillMonth && <>
                    <span className="mx-1">›</span>
                    <span className={poolDrillLevel === 'hour' ? 'cursor-pointer text-blue-500 hover:underline' : 'text-gray-700 font-semibold'}
                      onClick={() => poolDrillLevel === 'hour' && poolBackToDay()}>
                      {poolDrillMonth}
                    </span>
                  </>}
                  {poolDrillLevel === 'hour' && poolDrillDay && <>
                    <span className="mx-1">›</span>
                    <span className="text-gray-700 font-semibold">{poolDrillDay}</span>
                  </>}
                </span>
              </div>
            )}

            {poolDrillLevel === 'month' && (
              <MomHighlight
                bg="bg-blue-50" border="border-blue-100"
                items={[
                  { label: `ECMS · ${selectedMonth} vs ${prevMonth}`, change: kpi.momEcms,  value: formatAmount(kpi.ecms),  color: kpi.momEcms  >= 0 ? 'text-green-600' : 'text-red-600' },
                  { label: `iFleet · ${selectedMonth} vs ${prevMonth}`, change: kpi.momFleet, value: formatAmount(kpi.fleet), color: kpi.momFleet >= 0 ? 'text-green-600' : 'text-red-600' },
                  { label: `Combined · ${selectedMonth} vs ${prevMonth}`, change: kpi.momTotal, value: formatAmount(kpi.total), color: kpi.momTotal >= 0 ? 'text-green-600' : 'text-red-600' },
                ]}
              />
            )}

            {(trendLoading || poolDrillLoading) ? (
              <div className="h-44 bg-gray-50 rounded-lg animate-pulse" />
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart
                  data={poolDrillLevel === 'month' ? (poolZoomed ?? poolTrend) : poolDrillData ?? []}
                  margin={{ top: 8, right: 28, left: -16, bottom: 0 }}
                  onMouseDown={poolDrillLevel === 'month' ? handlePoolMouseDown : undefined}
                  onMouseMove={poolDrillLevel === 'month' ? handlePoolMouseMove : undefined}
                  onMouseUp={poolDrillLevel === 'month' ? handlePoolMouseUp : undefined}
                  onMouseLeave={poolDrillLevel === 'month' ? () => { poolIsDragging.current = false; setPoolRefLeft(''); setPoolRefRight(''); } : undefined}
                  onClick={poolDrillLevel !== 'hour' ? handlePoolChartClick : undefined}
                  style={{ cursor: poolDrillLevel === 'month' ? 'crosshair' : poolDrillLevel !== 'hour' ? 'pointer' : 'default' }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis
                    dataKey={poolDrillLevel === 'month' ? 'label' : poolDrillLevel === 'day' ? 'dayLabel' : 'hourLabel'}
                    tick={{ fontSize: poolDrillLevel === 'month' ? 8.5 : 8, fill: '#9ca3af', angle: poolDrillLevel === 'day' ? -45 : 0, textAnchor: poolDrillLevel === 'day' ? 'end' : 'middle' }}
                    interval={poolDrillLevel === 'day' ? 0 : poolDrillLevel === 'hour' ? 1 : 'preserveStartEnd'}
                    height={poolDrillLevel === 'day' ? 40 : 20}
                  />
                  <YAxis tickFormatter={yFormatter} tick={{ fontSize: 9, fill: '#9ca3af' }} />
                  <Tooltip
                    formatter={(v, name) => [`₹${v} Cr`, name === 'ecms' ? 'ECMS' : name === 'fleet' ? 'iFleet' : 'Combined']}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                  />
                  {(poolDrillLevel === 'month' ? (poolView === 'all' || poolView === 'ecms') : true) && (
                    <Line dataKey="ecms" stroke="#185FA5" strokeWidth={2.5} dot={{ r: 2.5, fill: '#fff', stroke: '#185FA5', strokeWidth: 1.5 }} activeDot={{ r: 5 }} name="ecms" />
                  )}
                  {(poolDrillLevel === 'month' ? (poolView === 'all' || poolView === 'fleet') : true) && (
                    <Line dataKey="fleet" stroke="#10b981" strokeWidth={2.5} dot={{ r: 2.5, fill: '#fff', stroke: '#10b981', strokeWidth: 1.5 }} activeDot={{ r: 5 }} name="fleet" />
                  )}
                  {poolDrillLevel === 'month' && poolView === 'all' && (
                    <Line dataKey="combined" stroke="#8b5cf6" strokeWidth={2} strokeDasharray="6 3" dot={false} name="combined" />
                  )}
                  {poolDrillLevel === 'month' && poolPeak && (poolView === 'all' || poolView === 'ecms') && (
                    <ReferenceDot x={poolPeak.label} y={poolPeak.ecms} r={5} fill="#059669" stroke="#fff" strokeWidth={2} label={{ value: '▲', position: 'top', fontSize: 10, fill: '#059669' }} />
                  )}
                  {poolDrillLevel === 'month' && poolLow && (poolView === 'all' || poolView === 'ecms') && (
                    <ReferenceDot x={poolLow.label} y={poolLow.ecms} r={5} fill="#fef3c7" stroke="#f59e0b" strokeWidth={2} label={{ value: '▼', position: 'bottom', fontSize: 10, fill: '#d97706' }} />
                  )}
                  {poolRefLeft && poolRefRight && poolRefLeft !== poolRefRight && (
                    <ReferenceArea x1={poolRefLeft} x2={poolRefRight} fill="#185FA5" fillOpacity={0.12} stroke="#185FA5" strokeOpacity={0.4} />
                  )}
                </LineChart>
              </ResponsiveContainer>
            )}
            {poolDrillLevel === 'month' && (
              <>
                <PeakLowStrip peak={poolPeak} low={poolLow} ecmsKey="ecms" fleetKey="fleet" peakBg="bg-green-50" peakBorder="border-green-200" />
                <MomHistoryStrip history={poolMomHistory} currentLabel={currentLabel} activeColor="bg-primary" />
              </>
            )}
          </div>

          {/* Platform Fee Trend */}
          <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
            <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
              <div>
                <div className="text-sm font-bold text-gray-900">Platform Fee Trend</div>
                {pfDrillLevel === 'month' && <div className="text-xs text-gray-400">12-month view · click a point to drill down</div>}
                {pfDrillLevel === 'day'   && <div className="text-xs text-gray-400">Day-wise · {pfDrillMonth}</div>}
                {pfDrillLevel === 'hour'  && <div className="text-xs text-gray-400">Hour-wise · {pfDrillDay}</div>}
              </div>
              <div className="flex flex-col items-end gap-2">
                {pfDrillLevel === 'month' && (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1 text-xs text-gray-600"><span className="inline-block w-4 h-0.5 bg-orange-500 rounded" />ECMS</div>
                    </div>
                    <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                      <ToggleBtn active={pfView==='all'}  label="Both" onClick={()=>setPfView('all')}  activeClass="bg-orange-500" />
                      <ToggleBtn active={pfView==='ecms'} label="ECMS" onClick={()=>setPfView('ecms')} activeClass="bg-orange-500" />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Zoom reset */}
            {pfDrillLevel === 'month' && pfZoomed && (
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-400">Zoomed view</span>
                <button onClick={() => setPfZoomed(null)}
                  className="text-xs text-orange-600 border border-orange-200 bg-orange-50 hover:bg-orange-100 rounded px-2 py-0.5 font-medium transition-colors">
                  ✕ Reset Zoom
                </button>
              </div>
            )}

            {/* Breadcrumb */}
            {pfDrillLevel !== 'month' && (
              <div className="flex items-center gap-2 mb-2 text-xs">
                <button onClick={pfBackToMonth} className="text-orange-500 hover:text-orange-700 font-medium">← Back</button>
                <span className="text-gray-400">
                  <span className="cursor-pointer text-orange-500 hover:underline" onClick={pfBackToMonth}>All Months</span>
                  {pfDrillMonth && <>
                    <span className="mx-1">›</span>
                    <span className={pfDrillLevel === 'hour' ? 'cursor-pointer text-orange-500 hover:underline' : 'text-gray-700 font-semibold'}
                      onClick={() => pfDrillLevel === 'hour' && pfBackToDay()}>
                      {pfDrillMonth}
                    </span>
                  </>}
                  {pfDrillLevel === 'hour' && pfDrillDay && <>
                    <span className="mx-1">›</span>
                    <span className="text-gray-700 font-semibold">{pfDrillDay}</span>
                  </>}
                </span>
              </div>
            )}

            {pfDrillLevel === 'month' && (
              <MomHighlight
                bg="bg-orange-50" border="border-orange-100"
                items={[
                  { label: `ECMS · ${selectedMonth} vs ${prevMonth}`, change: kpi.momPf, value: formatAmount(kpi.pf), color: kpi.momPf >= 0 ? 'text-orange-600' : 'text-red-600' },
                  { label: 'iFleet Platform Fee', change: null, value: 'N/A', color: 'text-gray-400' },
                  { label: `Total PF · ${selectedMonth}`, change: kpi.momPf, value: formatAmount(kpi.pf), color: kpi.momPf >= 0 ? 'text-orange-700' : 'text-red-600' },
                ]}
              />
            )}

            {(trendLoading || pfDrillLoading) ? (
              <div className="h-44 bg-gray-50 rounded-lg animate-pulse" />
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart
                  data={pfDrillLevel === 'month' ? (pfZoomed ?? pfTrend) : pfDrillData ?? []}
                  margin={{ top: 8, right: 28, left: -16, bottom: 0 }}
                  onMouseDown={pfDrillLevel === 'month' ? handlePfMouseDown : undefined}
                  onMouseMove={pfDrillLevel === 'month' ? handlePfMouseMove : undefined}
                  onMouseUp={pfDrillLevel === 'month' ? handlePfMouseUp : undefined}
                  onMouseLeave={pfDrillLevel === 'month' ? () => { pfIsDragging.current = false; setPfRefLeft(''); setPfRefRight(''); } : undefined}
                  onClick={pfDrillLevel !== 'hour' ? handlePfChartClick : undefined}
                  style={{ cursor: pfDrillLevel === 'month' ? 'crosshair' : pfDrillLevel !== 'hour' ? 'pointer' : 'default' }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis
                    dataKey={pfDrillLevel === 'month' ? 'label' : pfDrillLevel === 'day' ? 'dayLabel' : 'hourLabel'}
                    tick={{ fontSize: pfDrillLevel === 'month' ? 8.5 : 8, fill: '#9ca3af', angle: pfDrillLevel === 'day' ? -45 : 0, textAnchor: pfDrillLevel === 'day' ? 'end' : 'middle' }}
                    interval={pfDrillLevel === 'day' ? 0 : pfDrillLevel === 'hour' ? 1 : 'preserveStartEnd'}
                    height={pfDrillLevel === 'day' ? 40 : 20}
                  />
                  <YAxis tickFormatter={v => `${v}${pfDrillLevel === 'month' ? 'L' : 'Cr'}`} tick={{ fontSize: 9, fill: '#9ca3af' }} />
                  <Tooltip
                    formatter={(v, name) => [`₹${v} ${pfDrillLevel === 'month' ? 'L' : 'Cr'}`, name === 'ecms' ? 'ECMS PF' : name === 'amount' ? 'Platform Fee' : 'iFleet PF']}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                  />
                  {pfDrillLevel === 'month' ? (
                    (pfView === 'all' || pfView === 'ecms') && (
                      <Line dataKey="ecms" stroke="#f97316" strokeWidth={2.5} dot={{ r: 2.5, fill: '#fff', stroke: '#f97316', strokeWidth: 1.5 }} activeDot={{ r: 5 }} name="ecms" />
                    )
                  ) : (
                    <Line dataKey="amount" stroke="#f97316" strokeWidth={2.5} dot={{ r: 2.5, fill: '#fff', stroke: '#f97316', strokeWidth: 1.5 }} activeDot={{ r: 5 }} name="amount" />
                  )}
                  {pfDrillLevel === 'month' && pfPeak && (pfView === 'all' || pfView === 'ecms') && (
                    <ReferenceDot x={pfPeak.label} y={pfPeak.ecms} r={5} fill="#059669" stroke="#fff" strokeWidth={2} label={{ value: '▲', position: 'top', fontSize: 10, fill: '#059669' }} />
                  )}
                  {pfDrillLevel === 'month' && pfLow && (pfView === 'all' || pfView === 'ecms') && (
                    <ReferenceDot x={pfLow.label} y={pfLow.ecms} r={5} fill="#fef3c7" stroke="#f59e0b" strokeWidth={2} label={{ value: '▼', position: 'bottom', fontSize: 10, fill: '#d97706' }} />
                  )}
                  {pfRefLeft && pfRefRight && pfRefLeft !== pfRefRight && (
                    <ReferenceArea x1={pfRefLeft} x2={pfRefRight} fill="#f97316" fillOpacity={0.12} stroke="#f97316" strokeOpacity={0.4} />
                  )}
                </LineChart>
              </ResponsiveContainer>
            )}
            {pfDrillLevel === 'month' && (
              <>
                <PeakLowStrip peak={pfPeak} low={pfLow} ecmsKey="ecms" peakBg="bg-orange-50" peakBorder="border-orange-200" unit="L" />
                <MomHistoryStrip history={pfMomHistory} currentLabel={currentLabel} activeColor="bg-orange-500" />
              </>
            )}
          </div>
        </div>

        {/* ── Pool Account Loading — Day Wise (selected month) ── */}
        <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm mb-4">
          <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-sm font-bold text-gray-900">Pool Account Loading — Day Wise</div>
              <div className="text-xs text-gray-400">{selectedMonth} {selectedYear} · daily breakdown</div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 text-xs text-gray-600"><span className="inline-block w-3 h-3 rounded-sm bg-primary" />ECMS</div>
                <div className="flex items-center gap-1 text-xs text-gray-600"><span className="inline-block w-3 h-3 rounded-sm bg-green-500" />iFleet</div>
              </div>
              <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                <ToggleBtn active={poolDayView==='all'}   label="Both"   onClick={()=>setPoolDayView('all')}   activeClass="bg-primary" />
                <ToggleBtn active={poolDayView==='ecms'}  label="ECMS"   onClick={()=>setPoolDayView('ecms')}  activeClass="bg-primary" />
                <ToggleBtn active={poolDayView==='fleet'} label="iFleet" onClick={()=>setPoolDayView('fleet')} activeClass="bg-green-600" />
              </div>
            </div>
          </div>

          {poolDayLoading ? (
            <div className="h-48 bg-gray-50 rounded-lg animate-pulse" />
          ) : !poolDayTrend || poolDayTrend.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-gray-400 text-sm bg-gray-50 rounded-lg">
              No loading activity in {selectedMonth} {selectedYear}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={poolDayTrend} margin={{ top: 8, right: 28, left: -16, bottom: 36 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis
                  dataKey="dayLabel"
                  tick={{ fontSize: 8, fill: '#9ca3af', angle: -45, textAnchor: 'end' }}
                  interval={0}
                  height={44}
                />
                <YAxis tickFormatter={v => `${v}Cr`} tick={{ fontSize: 9, fill: '#9ca3af' }} />
                <Tooltip
                  formatter={(v, name) => [`₹${v} Cr`, name === 'ecms' ? 'ECMS' : name === 'fleet' ? 'iFleet' : 'Combined']}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
                {(poolDayView === 'all' || poolDayView === 'ecms') && (
                  <Line dataKey="ecms"  stroke="#185FA5" strokeWidth={2.5} dot={{ r: 2.5, fill: '#fff', stroke: '#185FA5', strokeWidth: 1.5 }} activeDot={{ r: 5 }} name="ecms" />
                )}
                {(poolDayView === 'all' || poolDayView === 'fleet') && (
                  <Line dataKey="fleet" stroke="#10b981" strokeWidth={2.5} dot={{ r: 2.5, fill: '#fff', stroke: '#10b981', strokeWidth: 1.5 }} activeDot={{ r: 5 }} name="fleet" />
                )}
                {poolDayView === 'all' && (
                  <Line dataKey="combined" stroke="#8b5cf6" strokeWidth={2} strokeDasharray="6 3" dot={false} name="combined" />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* ── Section B: Enterprise Loading Activity (selected month) ── */}
        <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm mb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-sm font-bold text-gray-900">Enterprise Loading Activity</div>
              <div className="text-xs text-gray-400">{selectedMonth} {selectedYear} — loaded vs not loaded</div>
            </div>
          </div>
          {monthLoading ? (
            <div className="grid grid-cols-3 gap-3">
              {[...Array(6)].map((_, i) => <div key={i} className="h-14 bg-gray-100 rounded-lg animate-pulse" />)}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {/* Loaded ≥ ₹1 */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded">✅ Loaded ≥ ₹1</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'ECMS',   value: monthStats?.loadedEcms,  color: 'text-primary' },
                    { label: 'iFleet', value: monthStats?.loadedFleet, color: 'text-green-600' },
                    { label: 'Total',  value: monthStats?.loadedTotal, color: 'text-gray-900', bold: true },
                  ].map(({ label, value, color, bold }) => (
                    <div key={label} className={`rounded-lg px-4 py-3 ${bold ? 'bg-green-50 border border-green-100' : 'bg-gray-50 border border-gray-100'}`}>
                      <div className="text-xs text-gray-500 font-medium mb-1">{label}</div>
                      <div className={`text-xl font-extrabold ${color}`}>{value ?? '—'}</div>
                      <div className="text-xs text-gray-400">enterprises</div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Not Loaded */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-semibold text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded">❌ Not Loaded</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'ECMS',   value: monthStats?.notLoadedEcms,  color: 'text-primary' },
                    { label: 'iFleet', value: monthStats?.notLoadedFleet, color: 'text-green-600' },
                    { label: 'Total',  value: monthStats?.notLoadedTotal, color: 'text-gray-900', bold: true },
                  ].map(({ label, value, color, bold }) => (
                    <div key={label} className={`rounded-lg px-4 py-3 ${bold ? 'bg-red-50 border border-red-100' : 'bg-gray-50 border border-gray-100'}`}>
                      <div className="text-xs text-gray-500 font-medium mb-1">{label}</div>
                      <div className={`text-xl font-extrabold ${color}`}>{value ?? '—'}</div>
                      <div className="text-xs text-gray-400">enterprises</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Section C: Platform Stats (onboarded + cards + users) ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">

          {/* Onboarded This Month */}
          <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
            <div className="text-sm font-bold text-gray-900 mb-1">Onboarded This Month</div>
            <div className="text-xs text-gray-400 mb-4">New enterprises in {selectedMonth} {selectedYear}</div>
            {monthLoading ? <div className="h-16 bg-gray-100 rounded-lg animate-pulse" /> : (
              <div className="flex flex-col gap-2">
                {[
                  { label: 'ECMS',   value: monthStats?.onboardedEcms,  color: 'text-primary' },
                  { label: 'iFleet', value: monthStats?.onboardedFleet, color: 'text-green-600' },
                  { label: 'Total',  value: monthStats?.onboardedTotal, color: 'text-gray-900', bold: true },
                ].map(({ label, value, color, bold }) => (
                  <div key={label} className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{label}</span>
                    <span className={`text-base font-extrabold ${color}`}>{value ?? '—'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Total Physical Cards */}
          <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
            <div className="text-sm font-bold text-gray-900 mb-1">Physical Cards</div>
            <div className="text-xs text-gray-400 mb-4">Active + physically linked</div>
            {liveLoading ? <div className="h-16 bg-gray-100 rounded-lg animate-pulse" /> : (
              <div className="flex flex-col gap-2">
                {[
                  { label: 'ECMS',   value: liveStats?.totalCardsEcms?.toLocaleString(),  color: 'text-primary' },
                  { label: 'iFleet', value: liveStats?.totalCardsFleet?.toLocaleString(), color: 'text-green-600' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{label}</span>
                    <span className={`text-base font-extrabold ${color}`}>{value ?? '—'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Total Users */}
          <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
            <div className="text-sm font-bold text-gray-900 mb-1">Total Users</div>
            <div className="text-xs text-gray-400 mb-4">Makers + Admins across both platforms</div>
            {liveLoading ? <div className="h-16 bg-gray-100 rounded-lg animate-pulse" /> : (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">ECMS Makers (Team Member)</span>
                  <span className="text-base font-extrabold text-primary">{liveStats?.ecmsTeamMembers?.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">ECMS Users (Non-Admin)</span>
                  <span className="text-base font-extrabold text-primary">{liveStats?.ecmsNonAdmins?.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">ECMS Admin (Admin and Co-Admin)</span>
                  <span className="text-base font-extrabold text-primary">{liveStats?.ecmsAdmins?.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between border-t border-gray-100 pt-2 mt-1">
                  <span className="text-xs text-gray-500">iFleet Makers (Team Member)</span>
                  <span className="text-base font-extrabold text-green-600">{liveStats?.fleetUserDetail?.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">iFleet Users (Non-Admin)</span>
                  <span className="text-base font-extrabold text-green-600">{liveStats?.fleetNonAdmins?.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">iFleet Admin (Admin and Co-Admin)</span>
                  <span className="text-base font-extrabold text-green-600">{liveStats?.fleetAdmins?.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between border-t border-gray-100 pt-2 mt-1">
                  <span className="text-xs font-semibold text-gray-700">Grand Total</span>
                  <span className="text-base font-extrabold text-gray-900">{liveStats?.grandTotalUsers?.toLocaleString()}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Top 5 Tables ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Top5Table
            title="Top 5 — ECMS Pool Loading"
            subtitle={`${selectedMonth} ${selectedYear} · ⚡ Redis cached`}
            data={top5Ecms}
            onDownload={handleDownloadEcms}
            colorClass={['bg-primary','bg-blue-500','bg-blue-400','bg-blue-300','bg-blue-200']}
            borderClass="bg-gradient-to-r from-primary to-blue-400"
          />
          <Top5Table
            title="Top 5 — iFleet Pool Loading"
            subtitle={`${selectedMonth} ${selectedYear} · ⚡ Redis cached`}
            data={top5Fleet}
            onDownload={handleDownloadFleet}
            colorClass={['bg-green-600','bg-green-500','bg-green-400','bg-green-300','bg-green-200']}
            borderClass="bg-gradient-to-r from-green-600 to-green-400"
          />
        </div>

      </div>
    </div>
  );
};

export default DashHome;
